<template>
    <div class="fill-height d-flex flex-column">
        <v-data-table
            :headers="headers"
            :items="workOrdersFiltered"
            class="elevation-0 flex-grow-1"
            :mobile-breakpoint="0"
            :loading="tableLoading"
            :loading-text="loadingText"
            :height="tableHeight"
            fixed-header
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Work Orders Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 py-0"
                    align="center"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" md="6" class="d-flex align-center mb-n2">
                        <h1 class="mr-4">UNNASIGNED WORK ORDERS</h1>
                        <v-btn
                            rounded
                            small
                            color="primary"
                            @click="findWO = true"
                        >
                            <v-icon class="mr-1">
                                mdi-database-search
                            </v-icon>
                            FIND
                        </v-btn>
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="d-flex justify-end mb-n2 pr-4"
                    >
                        <v-autocomplete
                            v-model="selectedDestinationFilters"
                            :items="destinationFilters"
                            label="Destination"
                            dense
                            multiple
                            chips
                            deletable-chips
                            small-chips
                            hide-details
                            style="max-width: 300px;"
                        >
                        </v-autocomplete>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item?.createdBy?.name || 'N/A' }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item && item.project ? item.project.reference : '' }}
                        - {{ item && item.project ? item.project.name : '' }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.productionStart`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.productionStart
                                ? formatDate(
                                      item.productionStart.seconds ||
                                          item.productionStart._seconds,
                                      'L'
                                  )
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.schedulingProgress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.schedulingProgress"
                        height="25"
                        :color="getProgressColor(item.schedulingProgress)"
                        rounded
                        class="no-interaction"
                    >
                        <strong
                            >{{ item.schedulingProgress.toFixed(2) }}%</strong
                        >
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.packingProgress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.packingProgress"
                        height="25"
                        :color="getProgressColor(item.packingProgress)"
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{ item.packingProgress.toFixed(2) }}%</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.shippingProgress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.shippingProgress"
                        height="25"
                        :color="getProgressColor(item.shippingProgress)"
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{ item.shippingProgress.toFixed(2) }}%</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openPlannerDetails(item)">
                        <v-icon>{{
                            editPermission ? 'mdi-pencil-plus' : 'mdi-magnify'
                        }}</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PLANNER DETAILS-->
        <v-dialog
            v-model="plannerDetails"
            :retain-focus="false"
            persistent
            width="1450px"
        >
            <WOPlannerDetails
                v-if="plannerDetails"
                :workOrder="selectedOrder"
                :user="user"
                :projects="projects"
                :packingLists="packingLists"
                @close="closePlannerDetails"
                @addPackingList="addPackingList"
                @replacePackingList="replacePackingList"
            />
        </v-dialog>
        <v-dialog
            v-model="findWO"
            :retain-focus="false"
            persistent
            max-width="600"
        >
            <FindWO
                v-if="findWO"
                @addWO="addWOFromFind"
                @close="findWO = false"
            />
        </v-dialog>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { firestore } from '@/services/firebase'
import WOMethods from '@/components/WorkOrders/WOMethods.js'

export default {
    name: 'UnnasignedWorkOrders',
    components: {
        WOPlannerDetails: () =>
            import('@/components/ShipmentPlanner/WOPlannerDetails.vue'),
        FindWO: () => import('@/components/WorkOrders/FindWO.vue'),
    },
    data: () => ({
        selectedOrder: undefined,
        plannerDetails: false,
        projectToFilter: undefined,
        projectToFilterDebounced: undefined,
        loading: false,
        workOrders: [],
        search: null,
        searchDebounced: null,
        destinationFilters: ['National', 'Export'],
        selectedDestinationFilters: [],
        companyCountry: 'colombia',
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PACKING PROGRESS',
                value: 'packingProgress',
                align: 'center',
                sortable: true,
                width: 200,
            },
            {
                text: 'SCHEDULING PROGRESS',
                value: 'schedulingProgress',
                align: 'center',
                sortable: true,
                width: 200,
            },
            {
                text: 'SHIPPING PROGRESS',
                value: 'shippingProgress',
                align: 'center',
                sortable: true,
                width: 200,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        listener: undefined,
        packingListsListener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        user: {},
        users: [],
        projects: [],
        editPermission: false,
        startDate: 0,
        queryStartDate: 0,
        deliveredStatus: [''],
        packingProcess: { id: 'packing', name: 'Packing' },
        // Constants
        STATUS_APPROVED: 'approved',
        STATUS_PENDING: 'pending',
        STATUS_IN_PROGRESS: 'inProgress',
        STATUS_REJECTED: 'rejected',
        STATUS_SHIPPED: 'shipped',
        STATUS_COMPLETED: 'completed',
        STATUS_FINISHED: 'finished',
        PROGRESS_THRESHOLDS: {
            ERROR: 25,
            ORANGE: 50,
            WARNING: 75,
            INFO: 100,
        },
        structuredWorkOrders: [],
        activateAlert: false,
        alertMessage: '',
        findWO: false,
        woMethods: new WOMethods(),
        packingLists: [],
        windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
        packingItemsCache: null,
        packingItemsIndexCache: null,
        isCalculating: false,
        hasPendingStructureData: false,
        isLoadingPackingLists: false,
    }),
    watch: {
        startDate: async function(newStartDate) {
            try {
                if (newStartDate > 0 && newStartDate !== this.queryStartDate) {
                    this.queryStartDate = newStartDate
                    // Unsubscribe from previous listener if exists
                    if (this.packingListsListener) {
                        this.packingListsListener()
                        this.packingListsListener = null
                    }
                    // Load packing lists in real-time
                    await this.getPackingListsFromSDK(newStartDate)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        packingLists: {
            handler: function() {
                // Re-structure data when packing lists change
                if (this.workOrders.length > 0) {
                    this.requestStructureData()
                }
            },
            deep: true,
        },
        search: function(newValue) {
            this.debouncedSearch(newValue)
        },
        projectToFilter: function(newValue) {
            this.debouncedProjectFilter(newValue)
        },
    },
    computed: {
        tableLoading() {
            return (
                this.loading || this.isCalculating || this.isLoadingPackingLists
            )
        },
        loadingText() {
            if (this.isLoadingPackingLists) return 'Loading packing lists...'
            if (this.isCalculating) return 'Calculating...'
            return 'Loading...'
        },
        sortedWorkOrders() {
            return [...this.structuredWorkOrders].sort(
                (a, b) =>
                    (b.createdOn?.seconds || b.createdOn?._seconds || 0) -
                    (a.createdOn?.seconds || a.createdOn?._seconds || 0)
            )
        },
        workOrdersFiltered() {
            let conditions = []
            conditions.push(this.filterByProgress)
            if (this.searchDebounced) {
                conditions.push(this.filterOrder)
            }
            if (this.projectToFilterDebounced) {
                conditions.push(this.filterProject)
            }
            if (this.selectedDestinationFilters.length > 0) {
                conditions.push(this.filterDestination)
            }
            if (conditions.length > 0) {
                return this.sortedWorkOrders.filter(order => {
                    return conditions.every(condition => {
                        return condition(order)
                    })
                })
            }
            return this.sortedWorkOrders
        },
        tableHeight() {
            const windowHeight = this.windowHeight || window.innerHeight
            const headerHeight = this.$vuetify.breakpoint.mdAndDown ? 80 : 70
            const padding = 40
            const calculatedHeight = windowHeight - headerHeight - padding
            const minHeight = 400
            const maxHeight = windowHeight * 0.9
            return (
                Math.max(minHeight, Math.min(calculatedHeight, maxHeight)) +
                'px'
            )
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user

            if (user.permissions.includes('updateWOProcesses')) {
                this.editPermission = true
            }
            this.projects = await API.getLiteProjects({ all: true })
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
            await this.getWOFromSDK()

            // Initialize packing lists listener after work orders are loaded
            // This will be triggered by the startDate watcher once getStartDate() sets it
            this.handleResize = () => {
                this.windowHeight = window.innerHeight
            }
            window.addEventListener('resize', this.handleResize)
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener) {
            this.listener()
            this.listener = null
        }
        if (this.packingListsListener) {
            this.packingListsListener()
            this.packingListsListener = null
        }
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize)
            this.handleResize = null
        }
        if (this.debouncedSearch?.cancel) this.debouncedSearch.cancel()
        if (this.debouncedProjectFilter?.cancel)
            this.debouncedProjectFilter.cancel()
        if (this.debouncedStructureData?.cancel)
            this.debouncedStructureData.cancel()
    },
    created() {
        this.debouncedSearch = _.debounce(value => {
            this.searchDebounced = value
        }, 400)
        this.debouncedProjectFilter = _.debounce(value => {
            this.projectToFilterDebounced = value
        }, 400)
        this.debouncedStructureData = _.debounce(() => {
            this.runStructureData()
        }, 50)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        invalidatePackingCaches() {
            this.packingItemsCache = null
            this.packingItemsIndexCache = null
        },
        requestStructureData() {
            this.invalidatePackingCaches()
            if (this.workOrders.length > 0) {
                this.debouncedStructureData()
            }
        },
        async runStructureData() {
            if (this.isCalculating) {
                this.hasPendingStructureData = true
                return
            }

            this.isCalculating = true
            await this.$nextTick()
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                await new Promise(resolve =>
                    window.requestAnimationFrame(resolve)
                )
            }
            try {
                this.structureData()
            } finally {
                this.isCalculating = false
                if (this.hasPendingStructureData) {
                    this.hasPendingStructureData = false
                    this.runStructureData()
                }
            }
        },
        openPlannerDetails(order) {
            this.selectedOrder = _.cloneDeep(order)
            this.plannerDetails = true
        },
        closePlannerDetails() {
            this.plannerDetails = false
        },
        async addWOFromFind(wo) {
            try {
                this.loading = true
                this.findWO = false
                const formatted = await this.formatWorkOrder(wo)
                if (!formatted) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message:
                            'Work order not found or not available for this view (approved/finished only).',
                    })
                    return
                }
                const index = this.workOrders.findIndex(
                    w => w.id === formatted.id
                )
                if (index >= 0) {
                    this.$set(this.workOrders, index, formatted)
                } else {
                    this.workOrders.push(formatted)
                }
                await this.runStructureData()
                this.$nextTick(() => {
                    const order = this.structuredWorkOrders.find(
                        w => w.id === formatted.id
                    )
                    if (order) {
                        this.openPlannerDetails(order)
                    }
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        filterByProgress(order) {
            return (
                order &&
                order.schedulingProgress !== undefined &&
                order.schedulingProgress < 100
            )
        },
        filterOrder(order) {
            return (
                order &&
                order.code &&
                order.code
                    .toLowerCase()
                    .includes(this.searchDebounced.toLowerCase())
            )
        },
        filterProject(order) {
            if (!order || !order.project) return false
            const filterLower = this.projectToFilterDebounced.toLowerCase()
            return (
                (order.project.name &&
                    order.project.name.toLowerCase().includes(filterLower)) ||
                (order.project.reference &&
                    order.project.reference
                        .toString()
                        .toLowerCase()
                        .includes(filterLower))
            )
        },
        filterDestination(order) {
            if (!order || !order.project || !order.project.country) return false
            const country = order.project.country.toLowerCase()

            if (
                country === this.companyCountry &&
                this.selectedDestinationFilters.includes('National')
            ) {
                return true
            }
            if (
                country !== this.companyCountry &&
                this.selectedDestinationFilters.includes('Export')
            ) {
                return true
            }
            return false
        },
        formatDate(seconds, format) {
            if (format === 'L') {
                return `${moment.unix(seconds).format('L')} ${moment
                    .unix(seconds)
                    .format('LT')}`
            } else {
                return `${moment.unix(seconds).format('YYYY-MM-DD')}`
            }
        },
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['rejected', 'approved', 'finished', 'deleted']
            )
        },
        async getWOFromSDK() {
            try {
                this.loading = true
                let startDate = new Date()
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('status', 'in', [
                        'rejected',
                        'approved',
                        'finished',
                        'deleted',
                        'closed',
                    ])
                    .where('updatedOn', '>=', startDate)

                // Load initial data from backup
                //let initialData = await this.loadAllActiveWorkOrders()

                let initialData = await API.getLatestWorkOrders({
                    status: 'approved,finished',
                })

                let data = []
                for (let wo of initialData) {
                    wo = await this.formatWorkOrder(wo)
                    if (wo) {
                        data.push(wo)
                    }
                }

                this.workOrders = data
                this.workOrders.forEach(wo => {
                    this.woMethods.formatItems(wo)
                })
                // Keep loading until the first heavy calculation completes.
                await this.runStructureData()

                this.listener = query.onSnapshot(
                    async snapshot => {
                        try {
                            const changes = Array.from(snapshot.docChanges())
                            const processedChanges = await Promise.all(
                                changes.map(async change => {
                                    try {
                                        let wo = Object.assign(
                                            change.doc.data(),
                                            {
                                                id: change.doc.id,
                                            }
                                        )
                                        wo = await this.formatWorkOrder(wo)
                                        return { change, wo }
                                    } catch (error) {
                                        console.error(
                                            'Error processing change:',
                                            error
                                        )
                                        return { change, wo: null }
                                    }
                                })
                            )

                            // Process all changes after all have been formatted
                            processedChanges.forEach(({ change, wo }) => {
                                if (wo) {
                                    if (
                                        change.type === 'added' ||
                                        change.type === 'modified'
                                    ) {
                                        const index = data.findIndex(
                                            w => w.id === wo.id
                                        )
                                        if (index >= 0) {
                                            // Update existing work order
                                            data[index] = wo
                                        } else {
                                            data.push(wo)
                                        }
                                    }
                                    if (
                                        change.type === 'removed' ||
                                        ![
                                            this.STATUS_APPROVED,
                                            this.STATUS_FINISHED,
                                        ].includes(wo.status)
                                    ) {
                                        const index = data.findIndex(
                                            w => w.id === wo.id
                                        )
                                        if (index >= 0) {
                                            data.splice(index, 1)
                                        }
                                    }
                                }
                            })
                            // Force array update to ensure reactivity
                            this.workOrders = [...data]
                            this.workOrders.forEach(wo => {
                                this.woMethods.formatItems(wo)
                            })

                            this.getStartDate()

                            // Structure data after work orders update
                            if (this.packingLists.length > 0) {
                                this.requestStructureData()
                                // update selectedOrder if it's currently open
                                // Use $nextTick to ensure structuredWorkOrders is updated
                                this.$nextTick(() => {
                                    if (
                                        this.selectedOrder &&
                                        this.selectedOrder.id
                                    ) {
                                        const updatedOrder = this.structuredWorkOrders.find(
                                            wo =>
                                                wo.id === this.selectedOrder.id
                                        )
                                        if (updatedOrder) {
                                            this.selectedOrder = _.cloneDeep(
                                                updatedOrder
                                            )
                                        }
                                    }
                                })
                            } else {
                                // If no packing lists, update from workOrders directly
                                if (
                                    this.selectedOrder &&
                                    this.selectedOrder.id
                                ) {
                                    const updatedOrder = this.workOrders.find(
                                        wo => wo.id === this.selectedOrder.id
                                    )
                                    if (updatedOrder) {
                                        this.selectedOrder = _.cloneDeep(
                                            updatedOrder
                                        )
                                    }
                                }
                            }
                        } catch (error) {
                            console.error(
                                'Error in onSnapshot callback:',
                                error
                            )
                            this.setErrorItems({
                                source: this.$options.name,
                                message: error.message,
                            })
                        }
                    },
                    error => {
                        // Handle snapshot errors
                        console.error('Firestore snapshot error:', error)
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async formatWorkOrder(wo) {
            try {
                const workOrder = wo
                workOrder.productionStartFormatted = workOrder.productionStart
                    ? workOrder.productionStart.seconds ||
                      workOrder.productionStart._seconds
                    : undefined
                workOrder.approvedDateFormatted = workOrder.approvedDate
                    ? workOrder.approvedDate.seconds ||
                      workOrder.approvedDate._seconds
                    : undefined

                const validStatuses = [
                    this.STATUS_REJECTED,
                    this.STATUS_APPROVED,
                    this.STATUS_FINISHED,
                    this.STATUS_DELETED,
                ]
                if (!validStatuses.includes(workOrder.status)) {
                    return undefined
                }

                // Filter by readAllWO permission in code (not in query)
                if (
                    !this.user?.permissions?.includes('readAllWO') &&
                    workOrder.createdBy != this.user.id
                ) {
                    return undefined
                }

                // set created by
                const user = this.users[workOrder.createdBy]
                if (user) {
                    workOrder.createdBy = user
                } else {
                    workOrder.createdBy = {
                        id: workOrder.createdBy,
                        name: '',
                        permissions: [],
                        process: [],
                    }
                    // add user if not exist
                    this.users[workOrder.createdBy] = workOrder.createdBy
                }

                // get or fetch project
                workOrder.project = this.projects.find(
                    project => project.id === workOrder.projectId
                )
                if (
                    !workOrder.project ||
                    Object.keys(workOrder.project).length === 0
                ) {
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    // add project to projects object
                    this.projects[workOrder.projectId] = workOrder.project
                }

                // Format items
                const formattedWO = this.woMethods.formatItems(workOrder)
                return formattedWO || workOrder
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                return undefined
            }
        },
        getStartDate() {
            let startDate = Date.now() / 1000
            this.workOrders.forEach(wo => {
                if (
                    wo.approvedDate &&
                    (wo.approvedDate._seconds || wo.approvedDate.seconds) <
                        startDate
                ) {
                    startDate =
                        wo.approvedDate._seconds || wo.approvedDate.seconds
                }
            })
            this.startDate = startDate
        },
        async getPackingListsFromSDK(startDateUnix) {
            this.isLoadingPackingLists = true
            try {
                // Load initial completed packing lists from API
                const startDateFormatted = moment
                    .unix(startDateUnix)
                    .format('YYYY-MM-DD')
                const completedPackingLists = await API.getPackingLists({
                    startDate: startDateFormatted,
                    status: 'completed',
                })
                this.packingLists = completedPackingLists || []

                // Invalidate cache
                this.invalidatePackingCaches()

                // Structure data after initial load
                if (this.workOrders.length > 0) {
                    this.requestStructureData()
                }

                // Set up real-time listener
                const startDate = moment.unix(startDateUnix).toDate()
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('packingLists')
                    .where('createdOn', '>=', startDate)

                // Keep "loading" until we receive the first snapshot callback.
                await new Promise((resolve, reject) => {
                    let firstSnapshot = true
                    this.packingListsListener = query.onSnapshot(
                        async snapshot => {
                            try {
                                const changes = Array.from(
                                    snapshot.docChanges()
                                )
                                let hasChanges = false

                                changes.forEach(change => {
                                    const packingList = Object.assign(
                                        change.doc.data(),
                                        {
                                            id: change.doc.id,
                                        }
                                    )

                                    // Filter by startDate in memory (Firestore where might not catch all)
                                    const scheduledForDate = packingList.scheduledFor
                                        ? new Date(packingList.scheduledFor)
                                        : null
                                    const requestedShipmentDate = packingList.requestedShipmentDate
                                        ? new Date(
                                              packingList.requestedShipmentDate
                                          )
                                        : null

                                    const isAfterStartDate =
                                        (scheduledForDate &&
                                            scheduledForDate >= startDate) ||
                                        (requestedShipmentDate &&
                                            requestedShipmentDate >=
                                                startDate) ||
                                        (!scheduledForDate &&
                                            !requestedShipmentDate)

                                    if (
                                        change.type === 'added' ||
                                        change.type === 'modified'
                                    ) {
                                        if (isAfterStartDate) {
                                            const index = this.packingLists.findIndex(
                                                pl => pl.id === packingList.id
                                            )
                                            if (index >= 0) {
                                                // Update existing packing list
                                                this.$set(
                                                    this.packingLists,
                                                    index,
                                                    packingList
                                                )
                                            } else {
                                                this.packingLists.push(
                                                    packingList
                                                )
                                            }
                                            hasChanges = true
                                        }
                                    }

                                    if (
                                        change.type === 'removed' &&
                                        packingList.status !== 'completed'
                                    ) {
                                        const index = this.packingLists.findIndex(
                                            pl => pl.id === packingList.id
                                        )
                                        if (index >= 0) {
                                            this.packingLists.splice(index, 1)
                                            hasChanges = true
                                        }
                                    }
                                })

                                if (hasChanges) {
                                    this.requestStructureData()
                                }
                            } catch (error) {
                                console.error(
                                    'Error in packing lists onSnapshot callback:',
                                    error
                                )
                                this.setErrorItems({
                                    source: this.$options.name,
                                    message: error.message,
                                })
                            } finally {
                                if (firstSnapshot) {
                                    firstSnapshot = false
                                    resolve()
                                }
                            }
                        },
                        error => {
                            console.error(
                                'Firestore packing lists snapshot error:',
                                error
                            )
                            this.setErrorItems({
                                source: this.$options.name,
                                message: error.message,
                            })
                            if (firstSnapshot) {
                                firstSnapshot = false
                                reject(error)
                            }
                        }
                    )
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.isLoadingPackingLists = false
            }
        },
        structureData() {
            const packingItems = this.getPackingItemsMemoized()
            const packingItemsIndex = this.getPackingItemsIndexMemoized(
                packingItems
            )
            const structuredWorkOrders = this.workOrders.map(workOrder => ({
                ...workOrder,
                items: (workOrder.items || []).map(item => ({ ...item })),
            }))
            structuredWorkOrders.forEach(workOrder => {
                this.processWorkOrderItems(workOrder, packingItemsIndex)
            })
            this.structuredWorkOrders = structuredWorkOrders
        },
        processWorkOrderItems(workOrder, packingItemsIndex) {
            workOrder.items = workOrder.items || []
            let totalPackableQty = 0
            let totalPackedQty = 0
            let totalScheduledQty = 0
            let totalShippedQty = 0
            const currentDate = new Date()

            workOrder.items.forEach(item => {
                const packingInformation =
                    packingItemsIndex[
                        this.getPackingItemKey(
                            workOrder.code,
                            item.partNumberId
                        )
                    ] || []
                const {
                    scheduledQty,
                    shippedQty,
                    totalPacked,
                    totalDelivered,
                    nextDelivery,
                } = this.calculatePackingInformationTotals(
                    packingInformation,
                    currentDate
                )
                item.packingInformation = packingInformation
                item.scheduledQty = scheduledQty
                item.shippedQty = shippedQty
                item.totalPacked = totalPacked
                item.totalDelivered = totalDelivered
                item.nextDelivery = nextDelivery

                // Acumular totales
                totalShippedQty += item.shippedQty
                totalScheduledQty += item.scheduledQty
                totalPackedQty += item.totalPacked

                if (
                    item.processes &&
                    item.processes.includes(this.packingProcess.id)
                ) {
                    totalPackableQty += item.quantity || 0
                }

                // Calcular progresos del item
                const hasPackingProcess =
                    item.processes &&
                    item.processes.includes(this.packingProcess.id)
                item.schedulingProgress = this.calculateProgress(
                    item.scheduledQty,
                    item.quantity,
                    hasPackingProcess
                )
                item.packingProgress = this.calculateProgress(
                    item.totalPacked,
                    item.quantity,
                    hasPackingProcess
                )
                item.shippingProgress = this.calculateProgress(
                    item.shippedQty,
                    item.quantity,
                    hasPackingProcess
                )
            })

            // Calcular progresos del work order
            const hasPackableItems = totalPackableQty > 0
            workOrder.schedulingProgress = this.calculateProgress(
                totalScheduledQty,
                totalPackableQty,
                hasPackableItems
            )
            workOrder.packingProgress = this.calculateProgress(
                totalPackedQty,
                totalPackableQty,
                hasPackableItems
            )
            workOrder.shippingProgress = this.calculateProgress(
                totalShippedQty,
                totalPackableQty,
                hasPackableItems
            )
        },
        getPackingItemKey(workOrderCode, partNumberId) {
            return `${workOrderCode || ''}::${partNumberId || ''}`
        },
        getPackingItemsIndexMemoized(packingItems) {
            if (this.packingItemsIndexCache) {
                return this.packingItemsIndexCache
            }

            const index = Object.create(null)
            packingItems.forEach(item => {
                const key = this.getPackingItemKey(
                    item.workOrder,
                    item.partNumberId
                )
                if (!index[key]) {
                    index[key] = []
                }
                index[key].push(item)
            })

            this.packingItemsIndexCache = index
            return index
        },
        calculatePackingInformationTotals(packingInformation, currentDate) {
            let scheduledQty = 0
            let shippedQty = 0
            let totalPacked = 0
            let totalDelivered = 0
            let nextDelivery = null

            packingInformation.forEach(register => {
                const packingQty = +register.packingQty || 0
                const suggestedQty = +register.suggestedQty || 0
                const status = register.status

                totalPacked += packingQty

                if (this.deliveredStatus.includes(status)) {
                    totalDelivered += packingQty
                }

                if (
                    status === this.STATUS_SHIPPED ||
                    status === this.STATUS_COMPLETED
                ) {
                    shippedQty += packingQty
                }

                if (register.scheduledFor) {
                    if (
                        status === this.STATUS_PENDING ||
                        status === this.STATUS_IN_PROGRESS ||
                        status === this.STATUS_REJECTED
                    ) {
                        scheduledQty += Math.max(suggestedQty, packingQty, 0)
                    } else {
                        scheduledQty += packingQty
                    }

                    const scheduledDate = new Date(register.scheduledFor)
                    if (
                        scheduledDate > currentDate &&
                        (nextDelivery === null || scheduledDate < nextDelivery)
                    ) {
                        nextDelivery = scheduledDate
                    }
                }
            })

            return {
                scheduledQty,
                shippedQty,
                totalPacked,
                totalDelivered,
                nextDelivery,
            }
        },
        calculateScheduledQty(packingInformation) {
            return packingInformation.reduce(
                (accum, register) =>
                    accum +
                    (register.scheduledFor
                        ? [
                              this.STATUS_PENDING,
                              this.STATUS_IN_PROGRESS,
                              this.STATUS_REJECTED,
                          ].includes(register.status)
                            ? Math.max(
                                  +register.suggestedQty,
                                  +register.packingQty,
                                  0
                              )
                            : register.packingQty || 0
                        : 0),
                0
            )
        },
        calculateShippedQty(packingInformation) {
            return packingInformation.reduce(
                (accum, register) =>
                    accum +
                    ([this.STATUS_SHIPPED, this.STATUS_COMPLETED].includes(
                        register.status
                    )
                        ? +register.packingQty || 0
                        : 0),
                0
            )
        },
        calculateNextDelivery(packingInformation) {
            let nextDelivery = null
            const currentDate = new Date()

            packingInformation.forEach(info => {
                if (info.scheduledFor) {
                    const scheduledDate = new Date(info.scheduledFor)
                    if (
                        scheduledDate > currentDate &&
                        (nextDelivery === null || scheduledDate < nextDelivery)
                    ) {
                        nextDelivery = scheduledDate
                    }
                }
            })

            return nextDelivery
        },
        getPackingItemsMemoized() {
            if (this.packingItemsCache) {
                return this.packingItemsCache
            }

            const packingItems = this.formatPackingItems()
            this.packingItemsCache = packingItems
            return packingItems
        },
        formatPackingItems() {
            let packingItems = []
            if (!this.packingLists || this.packingLists.length === 0) {
                return packingItems
            }
            this.packingLists.forEach(pl => {
                if (pl.packages && pl.packages.length > 0) {
                    pl.packages.forEach(packageElement => {
                        if (packageElement.items) {
                            packageElement.items.forEach(item => {
                                packingItems.push({
                                    partNumber: item.partNumber,
                                    partNumberId: item.partNumberId,
                                    packingQty: +item.packingQty || 0,
                                    status: pl.status,
                                    workOrder: item.workOrder,
                                    scheduledFor: pl.scheduledFor,
                                    packingListId: pl.id,
                                    packingListCode: pl.code,
                                    requestedShipmentDate:
                                        pl.requestedShipmentDate,
                                    airShipment: pl.airShipment
                                        ? pl.airShipment
                                        : false,
                                    suggestedQty: +item.suggestedQty || 0,
                                })
                            })
                        }
                    })
                } else if (
                    pl.selectedWorkOrders &&
                    pl.selectedWorkOrders.length > 0
                ) {
                    pl.selectedWorkOrders.forEach(workOrder => {
                        //findWorkOrder
                        const foundWorkOrder = this.workOrders.find(
                            wo => wo.code === workOrder.code
                        )
                        if (foundWorkOrder) {
                            foundWorkOrder.items.forEach(item => {
                                packingItems.push({
                                    partNumber: item.partNumber,
                                    partNumberId: item.partNumberId,
                                    packingQty: +item.quantity || 0,
                                    status: this.STATUS_COMPLETED,
                                    workOrder: workOrder.code,
                                    scheduledFor: pl.scheduledFor,
                                    packingListId: pl.id,
                                    packingListCode: pl.code,
                                    requestedShipmentDate:
                                        pl.requestedShipmentDate,
                                    airShipment: pl.airShipment
                                        ? pl.airShipment
                                        : false,
                                })
                            })
                        }
                    })
                }
            })
            return packingItems
        },
        // Helper functions
        calculateProgress(completed, total, hasProcess) {
            if (!hasProcess || !total || total === 0) return 100
            return ((completed || 0) * 100) / total
        },
        getProgressColor(percentage) {
            const { ERROR, ORANGE, WARNING, INFO } = this.PROGRESS_THRESHOLDS
            if (percentage < ERROR) return 'error'
            if (percentage < ORANGE) return 'orange'
            if (percentage < WARNING) return 'warning'
            if (percentage < INFO) return 'info'
            return 'success'
        },
        addPackingList({ alertMessage, packingList }) {
            if (!packingList) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Packing list is required',
                })
                return
            }
            this.packingLists.push(packingList)
            this.invalidatePackingCaches()
            this.activateAlert = true
            this.alertMessage = alertMessage
            this.plannerDetails = false
            this.requestStructureData()
        },
        replacePackingList({ packingList }) {
            if (!packingList || !packingList.id) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Invalid packing list provided',
                })
                return
            }
            const index = this.packingLists.findIndex(
                p => p.id === packingList.id
            )
            if (index >= 0) {
                this.packingLists.splice(index, 1, {
                    ...this.packingLists[index],
                    ...packingList,
                })
                this.requestStructureData()
            }
            this.plannerDetails = false
        },
    },
}
</script>

<style scoped>
.v-input .v-label {
    font-size: 12px;
}

.fill-height {
    height: 100%;
    min-height: 100%;
}
</style>
